/**
 * Fetch a workflow's definition (name + node list) for the run detail page.
 *
 * The canonical run detail page is keyed by run id and has no canvas, but the
 * header needs the workflow name and the step list / debug drawer need node
 * labels. This fetches `GET /workflows/:id` and maps the persisted nodes into
 * the frontend `WorkflowNode` shape the same way the canvas hydrator does
 * (`configJson` → `data`, `nodeType` → `type`).
 *
 * Step labels degrade gracefully without this (RunStepList falls back to
 * persisted step metadata), so callers can render before it resolves.
 *
 * @module hooks/useWorkflowNodes
 */

import { useQuery } from '@tanstack/react-query';
import type { WorkflowHealth } from '@chatsdet/types';
import { API_URL } from '../config.js';
import type { WorkflowNode } from '../types/workflow';

interface ApiWorkflowNode {
  id: string;
  nodeType?: string;
  configJson?: Record<string, unknown> | null;
}

interface WorkflowDetailResponse {
  workflow?: {
    name?: string;
    nodes?: ApiWorkflowNode[];
    health?: WorkflowHealth;
    environmentsPropertyId?: string | null;
  };
}

export interface WorkflowDefinition {
  name: string | null;
  nodes: WorkflowNode[];
  /** Reliability classification for the run-detail header health strip. */
  health: WorkflowHealth | null;
  /** The property operations (e.g. filing a manual issue) should be scoped to. */
  propertyId: string | null;
}

const EMPTY: WorkflowDefinition = { name: null, nodes: [], health: null, propertyId: null };

export function useWorkflowDefinition(workflowId: string | null): WorkflowDefinition {
  const query = useQuery<WorkflowDefinition>({
    queryKey: ['workflow-definition', workflowId],
    enabled: Boolean(workflowId),
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      if (!workflowId) return EMPTY;
      const res = await fetch(`${API_URL}/workflows/${workflowId}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load workflow');
      const json: WorkflowDetailResponse = await res.json();
      const wf = json.workflow;
      if (!wf) return EMPTY;
      return {
        name: wf.name ?? null,
        health: wf.health ?? null,
        propertyId: wf.environmentsPropertyId ?? null,
        nodes: (wf.nodes ?? []).map((node) => ({
          id: node.id,
          type: node.nodeType,
          data: (node.configJson ?? {}) as Record<string, unknown>,
        })),
      };
    },
  });

  return query.data ?? EMPTY;
}
